import {
  Patient,
  HealthRecord,
  VitalHistory,
  ImportantNote,
  ResidentVisitation,
  VisitStatus,
  VisitationInsertBody,
  VisitationStatusUpdateBody,
  ImportantNoteInsertBody,
  ImportantNoteEditBody,
  ImportantNoteUnflagBody,
} from './types';

export const parseDateString = (dateString: string): Date => {
  if (!dateString) return new Date();
  // Try parsing "YYYY-MM-DD HH:MM AM/PM" format first
  const longFormatMatch = dateString.match(/(\d{4})-(\d{2})-(\d{2}) (\d{1,2}):(\d{2}) (AM|PM)/);
  if (longFormatMatch) {
    const [, year, month, day, hourStr, minute, modifier] = longFormatMatch;
    let hour = parseInt(hourStr, 10);

    if (modifier === 'PM' && hour < 12) {
      hour += 12;
    }
    if (modifier === 'AM' && hour === 12) {
      hour = 0; // Midnight case
    }
    
    // Month is 0-indexed in JavaScript Date
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), hour, parseInt(minute));
  }
  
  // Try parsing "YYYY-MM-DD" format (from vital history) to avoid timezone issues
  const shortFormatMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (shortFormatMatch) {
      const [, year, month, day] = shortFormatMatch;
      // Using this constructor avoids timezone issues associated with parsing date-only strings
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }

  // Fallback for any other format that JS might handle correctly (e.g., ISO string)
  return new Date(dateString);
};

const mapEventTypeToRecordType = (eventType: string, sourceTable: string): HealthRecord['type'] => {
    const type = eventType.toLowerCase();
    if (type === 'vitals') return 'Vitals';
    if (type === 'adl') return 'ADL';
    if (type === 'notes') return 'Notes';
    if (type === 'emotion') return 'Emotion';
    if (type === 'gcs') return 'Glasgow Coma Scale (GCS)';
    if (type === 'pain') return 'Pain';
    if (type === 'photo' || (sourceTable && sourceTable.includes('photo'))) return 'Photos';
    if (sourceTable && sourceTable.includes('physio')) return 'Physiotherapy';
    if (sourceTable && sourceTable.includes('appointment')) return 'Appointment';
    if (type === 'input' || type === 'io' || (sourceTable && sourceTable.includes('input'))) return 'Input / Output';
    if (type === 'output' || (sourceTable && sourceTable.includes('output'))) return 'Input / Output';
    return 'Notes'; // Fallback
};

const formatApiTimestamp = (isoString: string): string => {
    const date = new Date(isoString);
    // API timestamps are stored as UTC+8 but parsed as UTC — subtract 8 hours to correct
    date.setHours(date.getHours() - 8);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const strHours = hours.toString().padStart(2, '0');
    return `${year}-${month}-${day} ${strHours}:${minutes} ${ampm}`;
};

const facilityMap: { [key: number]: string } = {
    1: 'The Mansion Gasing 136',
    2: 'The Mansion Seksyen 14',
    3: 'The Mansion Gasing 58',
    4: 'Komune Care',
    5: 'Tanjung Point',
    6: 'Home Care',
    7: 'The Mansion Ritchie',
    8: 'Xero Demo Centre',
    9: 'Angsana Home',
    10: 'Ara Woods',
    11: 'Jaya One Daycare',
};

const emotionCodeToName: Record<number, string> = {
    0: "Sad",
    1: "Upset",
    2: "Sleepy",
    3: "Confused",
    4: "Annoyed",
    5: "Angry",
    6: "Sick",
    7: "Restless",
    8: "Happy",
    9: "Very Happy",
    10: "Playful",
    11: "Content",
};

const mealCodeToName: Record<number, string> = {
    1: "Breakfast",
    2: "Lunch",
    3: "Tea Time",
    4: "Dinner",
    5: "Fruits",
    6: "Snack",
};

const outputCodeToName: Record<number, string> = {
    1: "Stool",
    2: "Urine",
};

const decodeFoodString = (foodString: string): string => {
    return foodString
        .split(",")
        .map((item) => {
            const [food, amount, unit] = item.split(";");
            return `${food} (${amount} ${unit})`;
        })
        .join(", ");
};


export const transformProfile = (profile: any): Patient => ({
    name: profile.full_name || 'N/A',
    age: profile.age || 0,
    id: profile.careid || profile.id.toString(),
    facility: facilityMap[profile.centre] || profile.room_name || 'N/A',
    avatarUrl: profile.profile_image_url || 'https://picsum.photos/seed/patient1/100/100',
});

export const transformHealthRecords = (apiRecords: any[]): HealthRecord[] => {
    if (!apiRecords) return [];
    return apiRecords.map((record): HealthRecord => {
        let summary = record.event_details;
        let images: string[] | undefined = undefined;
        const type = mapEventTypeToRecordType(record.event_type, record.source_table);

        if (type === 'Emotion') {
            const detail = record.event_details;
            if (!isNaN(parseInt(detail, 10))) {
                summary = `Patient feels ${emotionCodeToName[parseInt(detail, 10)] ?? detail}.`;
            } else {
                summary = `Patient feels ${detail?.charAt(0).toUpperCase() + detail?.slice(1)}.`;
            }
        } else if (type === 'Vitals' && record.event_details) {
            let detail: string;
            try {
                detail = decodeURIComponent(record.event_details);
            } catch {
                detail = record.event_details;
            }
            detail = detail
                .replace('Heart Rate', 'HR')
                .replace('Blood Pressure', 'BP')
                .replace('Temperature', 'Temp')
                .replace('Oxygen', 'SpO2')
                .replace('Respiratory', 'Respiration');
            summary = detail.replace(/ (bpm|mmHg|℃|°C|%|breaths\/min|mmol\/L)/gi, '');
        } else if (type === 'Input / Output') {
            const details = record.event_details || '';
            const sourceTable = (record.source_table || '').toLowerCase();

            if (sourceTable.includes('output') || details.startsWith('Output Type:')) {
                // Output record: "Output Type: 1"
                const outputMatch = details.match(/Output Type:\s*(\d+)/);
                if (outputMatch) {
                    const code = parseInt(outputMatch[1], 10);
                    summary = outputCodeToName[code] ?? details;
                }
            } else {
                // Input record — two possible formats from the backend:
                //
                // Legacy: "Input: bihun goreng;1;unit,Milo;1;unit"
                //
                // New:    "Meal: [Breakfast] ( Feeding Type: [Normal] Details: [food (1 unit)] ) ( Notes: [] undefined)"
                //          ↑ The backend incorrectly appends the word "undefined" when the
                //            notes field is empty. Sanitize it here so it never reaches the UI.

                if (details.startsWith('Meal: [')) {
                    // New format — strip the orphaned "undefined" text and any empty
                    // Notes section left behind after the removal.
                    summary = details
                        .replace(/\bundefined\b/gi, '')        // remove literal word "undefined"
                        .replace(/\(\s*Notes:\s*\[\]\s*\)/g, '') // remove empty "( Notes: [] )"
                        .replace(/\s{2,}/g, ' ')               // collapse double-spaces
                        .trim();
                } else {
                    // Legacy encoded format: "Input: bihun goreng;1;unit,Milo;1;unit"
                    const parts: string[] = [];

                    // Handle meal code if present as a separate field on the raw record
                    const mealCode = parseInt(record.meal, 10);
                    if (!isNaN(mealCode) && mealCodeToName[mealCode]) {
                        parts.push(`Having ${mealCodeToName[mealCode]}`);
                    }

                    // Strip "Input: " prefix and decode the semicolon-encoded food string
                    const foodPart = details.replace(/^Input:\s*/, '').trim();
                    if (foodPart && foodPart.includes(';')) {
                        parts.push(decodeFoodString(foodPart));
                    } else if (foodPart) {
                        parts.push(foodPart);
                    }

                    if (parts.length > 0) {
                        summary = parts.join(' - ');
                    } else {
                        summary = 'Input recorded (no details)';
                    }
                }
            }
        } else if (type === 'Photos') {
            const url = record.event_details;
            if (url && typeof url === 'string' && url !== 'null' && url.startsWith('http')) {
                images = [url];
                summary = 'Image Gallery';
            } else {
                images = undefined;
                summary = 'Photo record found, but the image URL is missing.';
            }
        }

        return {
            id: record.id.toString(),
            date: formatApiTimestamp(record.event_timestamp),
            recordedBy: record.creator_name,
            type: type,
            summary: summary || '',
            images: images,
        };
    });
};

const toLocalDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Returns a YYYY-MM-DD string built from the LOCAL calendar components of `date`.
 *
 * This helper is the primary fix for the AppointmentPanel save bug: previous
 * code computed `scheduled_date` via `selectedDate.toISOString().split('T')[0]`,
 * which converts the locally-selected date to UTC and produces an off-by-one
 * date in non-UTC timezones. This helper MUST NOT call
 * `Date.prototype.toISOString()` (or any other UTC-based serialization).
 *
 * Every visitation insert call site must route the picked Date through this
 * helper instead of `toISOString().split('T')[0]`.
 */
export const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const MM = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${year}-${MM}-${dd}`;
};

export const transformVitalHistory = (apiVitals: any): VitalHistory[] => {
    // Local intermediate type: bp can be null while constructing; final VitalHistory must have numeric values.
    type LocalEntry = {
        date: string;
        bp: { systolic: number | null; diastolic: number | null };
        hr: number | null;
        glucose: number | null;
        weight: number | null;
    };

    const vitalsByDate = new Map<string, LocalEntry>();

    const processVitalType = (data: any[] | undefined, updateFn: (entry: LocalEntry, record: any) => void) => {
        if (!data) return;
        const sortedData = [...data].sort((a, b) => new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime());
        sortedData.forEach(record => {
            if (!record || !record.recorded_at) return;
            const recordDate = new Date(record.recorded_at);
            // API timestamps are UTC+8 but parsed as UTC — subtract 8 hours
            recordDate.setHours(recordDate.getHours() - 8);
            const dateKey = toLocalDateKey(recordDate);
            const existing = vitalsByDate.get(dateKey);
            const entry: LocalEntry = existing ?? {
                date: dateKey,
                bp: { systolic: null, diastolic: null },
                hr: null,
                glucose: null,
                weight: null,
            };
            updateFn(entry, record);
            vitalsByDate.set(dateKey, entry);
        });
    };

    processVitalType(apiVitals?.blood_pressure, (entry, record) => {
        const systolic = record?.bp_systolic ?? null;
        // Allow for typo in API: bp_distolic
        const diastolic = record?.bp_distolic ?? record?.bp_diastolic ?? null;
        entry.bp.systolic = typeof systolic === 'number' ? systolic : (systolic ? Number(systolic) : entry.bp.systolic);
        entry.bp.diastolic = typeof diastolic === 'number' ? diastolic : (diastolic ? Number(diastolic) : entry.bp.diastolic);
    });
    processVitalType(apiVitals?.heart_rate, (entry, record) => {
        const v = record?.heart_rate;
        entry.hr = v == null ? entry.hr : Number(v);
    });
    processVitalType(apiVitals?.glucose, (entry, record) => {
        const v = record?.glucose;
        if (v != null && !isNaN(Number(v))) {
            // If value is in mmol/L, convert; if already mg/dL skip conversion heuristic is not perfect,
            // but keep current behaviour: assume mmol/L and convert.
            entry.glucose = Math.round(Number(v) * 18.0182);
        }
    });
    processVitalType(apiVitals?.weight, (entry, record) => {
        const v = record?.weight;
        entry.weight = v == null ? entry.weight : Number(v);
    });

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29);
    thirtyDaysAgo.setHours(0, 0, 0, 0);

    // If there is no real vital data at all, return an empty array
    // so the UI can show placeholder values (--/-- mmHg, -- bpm, etc.)
    if (vitalsByDate.size === 0) {
        return [];
    }

    const completeHistory: VitalHistory[] = [];
    let lastValid: LocalEntry | null = null;

    for (let i = 0; i < 30; i++) {
        const currentDate = new Date(thirtyDaysAgo);
        currentDate.setDate(currentDate.getDate() + i);
        const dateKey = toLocalDateKey(currentDate);
        const apiData = vitalsByDate.get(dateKey) ?? null;

        if (apiData) lastValid = apiData;

        // Compose the values using lastValid where needed, and fallback to 0 (not hardcoded normal values)
        const use = lastValid ?? {
            date: dateKey,
            bp: { systolic: null, diastolic: null },
            hr: null,
            glucose: null,
            weight: null,
        };

        completeHistory.push({
            date: dateKey,
            bp: {
                systolic: use.bp.systolic ?? 0,
                diastolic: use.bp.diastolic ?? 0,
            },
            hr: use.hr ?? 0,
            glucose: use.glucose ?? 0,
            weight: use.weight ?? 0,
        });
    }

    // Ensure today's entry reflects last actual values if available (keeps today's date)
    if (completeHistory.length && lastValid) {
        const idx = completeHistory.length - 1;
        const todayDate = completeHistory[idx].date;
        completeHistory[idx] = {
            date: todayDate,
            bp: {
                systolic: lastValid.bp.systolic ?? 0,
                diastolic: lastValid.bp.diastolic ?? 0,
            },
            hr: lastValid.hr ?? 0,
            glucose: lastValid.glucose ?? 0,
            weight: lastValid.weight ?? 0,
        };
    }

    return completeHistory;
};

/**
 * Normalize a `status` string from the visitation API to the title-case
 * front-end literal. Accepts any casing of `"scheduled"` / `"completed"`;
 * returns `null` for any other input (callers filter those rows out).
 */
export const normalizeVisitStatus = (
  s: string | undefined | null,
): VisitStatus | null => {
  if (typeof s !== 'string') return null;
  const lowered = s.trim().toLowerCase();
  if (lowered === 'scheduled') return 'Scheduled';
  if (lowered === 'completed') return 'Completed';
  return null;
};

/**
 * Coerce an unknown value to a finite number. Accepts `number` directly and
 * numeric strings (via `Number(...)`). Returns `null` for anything else.
 */
const toFiniteNumber = (v: unknown): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

/**
 * Parse the `important_notes` array from `HcGetCustomerInfoById`. Tolerates
 * missing/null/non-array input by returning `[]`. Malformed elements are
 * omitted without throwing.
 */
export const parseImportantNotes = (raw: unknown): ImportantNote[] => {
  if (!Array.isArray(raw)) return [];
  const result: ImportantNote[] = [];
  for (const elem of raw) {
    if (!elem || typeof elem !== 'object') continue;
    const e = elem as Record<string, unknown>;
    const id = toFiniteNumber(e.id);
    if (id === null) continue;
    if (typeof e.notes !== 'string') continue;
    if (typeof e.recorded_at !== 'string') continue;
    if (typeof e.creator_name !== 'string') continue;
    result.push({
      id,
      notes: e.notes,
      recorded_at: e.recorded_at,
      creator_name: e.creator_name,
    });
  }
  return result;
};

/**
 * Parse the `resident_visitation` array from `HcGetCustomerInfoById`.
 * Tolerates missing/null/non-array input by returning `[]`. Malformed
 * elements (or rows whose status casing matches neither "scheduled" nor
 * "completed") are omitted without throwing; a `console.warn` is emitted
 * for each such filtered row.
 *
 * `scheduled_time_slot` is passed through verbatim with NO membership
 * validation against `Time_Slot_Constants`. The value may be a point-in-time
 * string (`"10:00 AM"`) or a range (`"10:00-11:00"`, `"10:00 AM-11:00 PM"`).
 */
export const parseResidentVisitations = (raw: unknown): ResidentVisitation[] => {
  if (!Array.isArray(raw)) return [];
  const result: ResidentVisitation[] = [];
  for (const elem of raw) {
    if (!elem || typeof elem !== 'object') continue;
    const e = elem as Record<string, unknown>;

    const id = toFiniteNumber(e.id);
    if (id === null) continue;
    const customer_id = toFiniteNumber(e.customer_id);
    if (customer_id === null) continue;

    if (typeof e.scheduled_date !== 'string') continue;
    if (typeof e.scheduled_time_slot !== 'string') continue;
    if (typeof e.created_at !== 'string') continue;
    if (typeof e.updated_at !== 'string') continue;
    if (typeof e.customer_name !== 'string') continue;

    // created_by_employee_id: number or null
    let created_by_employee_id: number | null;
    if (e.created_by_employee_id === null || e.created_by_employee_id === undefined) {
      created_by_employee_id = null;
    } else {
      const n = toFiniteNumber(e.created_by_employee_id);
      if (n === null) continue;
      created_by_employee_id = n;
    }

    // notes: string or null
    let notes: string | null;
    if (e.notes === null || e.notes === undefined) {
      notes = null;
    } else if (typeof e.notes === 'string') {
      notes = e.notes;
    } else {
      continue;
    }

    // created_by_name: string or null
    let created_by_name: string | null;
    if (e.created_by_name === null || e.created_by_name === undefined) {
      created_by_name = null;
    } else if (typeof e.created_by_name === 'string') {
      created_by_name = e.created_by_name;
    } else {
      continue;
    }

    // status: pass through normalizeVisitStatus; omit row if it returns null
    const rawStatus = typeof e.status === 'string' ? e.status : null;
    const status = normalizeVisitStatus(rawStatus);
    if (status === null) {
      // eslint-disable-next-line no-console
      console.warn(
        `parseResidentVisitations: unrecognized status casing for visitation id=${id}: ${JSON.stringify(e.status)}`,
      );
      continue;
    }

    result.push({
      id,
      customer_id,
      scheduled_date: e.scheduled_date,
      scheduled_time_slot: e.scheduled_time_slot,
      status,
      notes,
      created_by_employee_id,
      created_at: e.created_at,
      updated_at: e.updated_at,
      customer_name: e.customer_name,
      created_by_name,
    });
  }
  return result;
};

export interface VisitationInsertInput {
  patientId: string;            // numeric customer id as a string
  scheduledDate: Date;          // the locally-picked Date object from the calendar
  scheduledTimeSlot: string;    // value from weekdayAppointmentSlots ∪ weekendAppointmentSlots
  notes: string;                // body field "notes" — UI label is "Remarks (optional)"
  userRole: 'staff' | 'family';
  employeeNumber: string | null;
}

/**
 * Build the body for inserting a new visitation row.
 *
 * `scheduled_date` MUST be derived from `formatLocalDate(input.scheduledDate)`
 * to preserve the locally-picked calendar date; do NOT use `toISOString()`.
 *
 * `created_by_employee_id` is ALWAYS present in the body:
 *   - `staff`  -> `parseInt(employeeNumber, 10)` (or `null` if employeeNumber
 *                 is null or non-numeric).
 *   - `family` -> `null`.
 */
export const buildVisitationInsert = (
  input: VisitationInsertInput,
): VisitationInsertBody => {
  const customerId = parseInt(input.patientId, 10);
  let createdByEmployeeId: number | null = null;
  if (input.userRole === 'staff' && input.employeeNumber !== null) {
    const parsed = parseInt(input.employeeNumber, 10);
    createdByEmployeeId = Number.isFinite(parsed) ? parsed : null;
  }
  return {
    customer_id: customerId,
    scheduled_date: formatLocalDate(input.scheduledDate),
    scheduled_time_slot: input.scheduledTimeSlot,
    status: 'Scheduled',
    notes: input.notes,
    created_by_employee_id: createdByEmployeeId,
  };
};

/**
 * Build the body for marking an existing visitation as completed.
 * Returns exactly `{ id, customer_id, status: 'Completed' }`.
 */
export const buildVisitationStatusUpdate = (
  id: number,
  customerId: number,
): VisitationStatusUpdateBody => ({
  id,
  customer_id: customerId,
  status: 'Completed',
});

/**
 * Build the body for inserting a new note via HcUpdateHealthJournal.
 * `customer_id` and `created_by` are single-element arrays, matching the
 * existing `HcUpdateHealthJournal` convention used by the regular note save.
 * No `type` field is sent.
 */
export const buildImportantNoteInsert = (
  customerId: number,
  employeeId: number,
  notes: string,
  important: 0 | 1,
): ImportantNoteInsertBody => ({
  customer_id: [customerId],
  notes,
  table: 'h_note',
  created_by: [employeeId],
  important,
});

/**
 * Build the body for inline-editing an existing note's text.
 * Body is exactly `{ id, customer_id, table: 'h_note', notes }` — no `type`.
 */
export const buildImportantNoteEditBody = (
  id: number,
  customerId: number,
  notes: string,
): ImportantNoteEditBody => ({
  id,
  customer_id: customerId,
  table: 'h_note',
  notes,
});

/**
 * Build the body for removing the important flag from an existing note.
 * Body is exactly `{ id, customer_id, table: 'h_note', important: 0 }` — no `type`.
 */
export const buildImportantNoteUnflagBody = (
  id: number,
  customerId: number,
): ImportantNoteUnflagBody => ({
  id,
  customer_id: customerId,
  table: 'h_note',
  important: 0,
});
